import type { InboxItem } from "../../.generated/api-client";
import type { HearthTagSemantic } from "./HearthTag";

export const ADMIN_INBOX_REFRESH_EVENT = "ember-admin-inbox-refresh";

export function adminInboxCategoryLabel(category: string): string {
  const labels: Record<string, string> = {
    approval: "审批",
    schedule: "排班",
    inspection: "巡检",
    photo_compliance: "卫生自查",
    customer_voice: "客诉",
    inventory: "盘点",
    system: "通知",
  };
  return labels[category] ?? "消息";
}

export function adminInboxCategoryTone(category: string): HearthTagSemantic {
  if (category === "schedule") return "success";
  if (
    category === "inspection" ||
    category === "photo_compliance" ||
    category === "inventory" ||
    category === "customer_voice"
  )
    return "warning";
  if (category === "approval") return "info";
  return "neutral";
}

function queryId(path: string | null, names: string[]): string | null {
  if (!path) return null;
  try {
    const url = new URL(path, "https://ember.invalid");
    for (const name of names) {
      const value = url.searchParams.get(name);
      if (value) return value;
    }
    return null;
  } catch {
    return null;
  }
}

function positiveId(value: string | null): string | null {
  return value !== null && /^\d+$/.test(value) && Number(value) > 0
    ? value
    : null;
}

/**
 * Consume the server-validated Admin target instead of rebuilding a module
 * route from category alone. Query names are adapted only where the existing
 * Admin workbench has a frozen public parameter name.
 */
function registeredAdminRoute(path: string | null): string | null {
  if (!path) return null;
  try {
    const url = new URL(path, "https://ember.invalid");
    if (url.pathname === "/approvals") {
      const requestId = positiveId(
        url.searchParams.get("request_id") ??
          url.searchParams.get("approvalId"),
      );
      return requestId
        ? `/approvals?approvalId=${encodeURIComponent(requestId)}`
        : null;
    }
    if (url.pathname === "/inventory") {
      const taskId = positiveId(url.searchParams.get("task_id"));
      return taskId ? `/inventory?task_id=${encodeURIComponent(taskId)}` : null;
    }
    if (url.pathname === "/inspections") {
      const inspectionId = positiveId(
        url.searchParams.get("id") ?? url.searchParams.get("inspectionId"),
      );
      return inspectionId
        ? `/inspections?inspectionId=${encodeURIComponent(inspectionId)}`
        : null;
    }
    if (url.pathname === "/employee-onboarding") {
      const requestId = positiveId(url.searchParams.get("request_id"));
      return requestId
        ? `/employee-onboarding?request_id=${encodeURIComponent(requestId)}`
        : null;
    }
    if (url.pathname === "/customer-voice") {
      const ticketId = positiveId(
        url.searchParams.get("ticket_id") ?? url.searchParams.get("ticketId"),
      );
      return ticketId
        ? `/customer-voice?tab=tickets&ticketId=${encodeURIComponent(ticketId)}`
        : null;
    }
    if (url.pathname === "/attendance-amendment") {
      const punchDate = url.searchParams.get("date");
      return punchDate && /^\d{4}-\d{2}-\d{2}$/.test(punchDate)
        ? `/attendance-amendment?date=${encodeURIComponent(punchDate)}`
        : null;
    }
    return null;
  } catch {
    return null;
  }
}

export function adminRouteForInboxItem(
  item: Pick<
    InboxItem,
    | "action_item_id"
    | "category"
    | "deeplink_path"
    | "source_table"
    | "source_id"
    | "store_id"
  >,
): string | null {
  const registered = registeredAdminRoute(item.deeplink_path);
  if (registered) return registered;

  // A platform ActionItem with no accepted Admin route is a real
  // deeplink_failed edge. Do not disguise it as a generic module jump.
  if (item.action_item_id != null && !item.deeplink_path) return null;

  if (item.category === "schedule") return "/scheduling";
  if (item.category === "photo_compliance") return "/inspections?tab=photo";
  if (item.category === "inspection") return "/inspections";
  if (item.category === "inventory") return "/inventory";

  if (
    item.category === "customer_voice" ||
    item.source_table === "ops.customer_voice_ticket"
  ) {
    const ticketId =
      queryId(item.deeplink_path, ["ticket_id", "ticketId"]) ?? item.source_id;
    if (!/^\d+$/.test(ticketId)) return "/customer-voice?tab=tickets";
    const params = new URLSearchParams({
      tab: "tickets",
      ticketId,
    });
    if (typeof item.store_id === "number" && item.store_id > 0) {
      params.set("storeId", String(item.store_id));
    }
    return `/customer-voice?${params.toString()}`;
  }

  if (
    item.source_table === "work_request" ||
    item.source_table === "ops.work_request"
  ) {
    const requestId =
      queryId(item.deeplink_path, ["request_id", "approvalId"]) ??
      item.source_id;
    return /^\d+$/.test(requestId)
      ? `/approvals?approvalId=${encodeURIComponent(requestId)}`
      : "/approvals";
  }

  return null;
}
